import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewEncapsulation,
  computed,
  effect,
  inject,
} from '@angular/core';

import { TabTrapDirective } from '@shared/directives/tab-trap.directive';

import { DialogFrame } from '../dialog-frame/dialog-frame';
import { DialogService } from '../dialog.service';
import { DialogInstance, DialogPosition } from '../dialog.models';

/** Toggled on <html> for the duration of the stack. See dialog-host.scss. */
const SCROLL_LOCK_CLASS = 'emg-dialog-open';

/** The scrim is the flex container, so position is pure alignment. Plain geometry. */
const POSITION_ALIGN: Record<DialogPosition, { align: string; justify: string }> = {
  center: { align: 'center', justify: 'center' },
  top: { align: 'flex-start', justify: 'center' },
  bottom: { align: 'flex-end', justify: 'center' },
  left: { align: 'center', justify: 'flex-start' },
  right: { align: 'center', justify: 'flex-end' },
};

/**
 * The single overlay host. One instance sits in the root component; it renders
 * the whole DialogService stack.
 *
 * It owns EXACTLY ONE document keydown listener for Escape. One listener per
 * frame would fire once per open dialog and collapse the entire stack on a
 * single keypress - which is precisely the defect the contract calls out.
 *
 * Encapsulation is None on purpose: the body scroll-lock rule targets <html>,
 * which no scoped stylesheet can reach, and this component is a singleton so
 * there is nothing for its two prefixed rules to collide with.
 */
@Component({
  selector: 'app-dialog-host',
  imports: [DialogFrame, TabTrapDirective],
  templateUrl: './dialog-host.html',
  styleUrl: './dialog-host.scss',
  encapsulation: ViewEncapsulation.None,
  host: { '(document:keydown)': 'onKeydown($event)' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogHost {
  private readonly dialogs = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly stack = this.dialogs.stack;
  protected readonly topIndex = computed(() => this.stack().length - 1);

  constructor() {
    // overflow:hidden with scrollbar-gutter:stable - hiding the scrollbar
    // without reserving its gutter shifts the whole page left on open.
    effect(() => {
      document.documentElement.classList.toggle(SCROLL_LOCK_CLASS, this.dialogs.hasOpen());
    });

    this.destroyRef.onDestroy(() => {
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    });
  }

  /** scrims at 1000 + i*10, panels at 1001 + i*10: dialog n+1 clears dialog n's backdrop. */
  protected scrimZ(index: number): number {
    return 1000 + index * 10;
  }

  protected panelZ(index: number): number {
    return 1001 + index * 10;
  }

  protected align(instance: DialogInstance): string {
    return POSITION_ALIGN[instance.config.position ?? 'center'].align;
  }

  protected justify(instance: DialogInstance): string {
    return POSITION_ALIGN[instance.config.position ?? 'center'].justify;
  }

  /**
   * Backdrop clicks resolve against the clicked instance only, and only when
   * that instance opted in. Default is false, because a half-typed form must
   * not evaporate on a stray click.
   */
  protected onBackdrop(event: MouseEvent, instance: DialogInstance): void {
    if (event.target !== event.currentTarget) return;
    if (instance.config.closeOnBackdrop !== true) return;
    void this.dialogs.requestClose(instance, 'backdrop');
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !this.dialogs.hasOpen()) return;
    // closeTop() checks the top instance's closeOnEsc and awaits its
    // canClose(), so a veto leaves the stack untouched.
    event.preventDefault();
    void this.dialogs.closeTop('esc');
  }
}
